import { useTranslation } from 'react-i18next'
import { feeBreakdown } from '../../settings/paymentFees'
import { formatMoney } from '../../products/money'

const STRIPE_COLOR = '#635BFF'
const PLATFORM_COLOR = '#1f2937'

/**
 * What the gym keeps from one entry fee paid by card: the same three-way split,
 * the same numbers and the same words as the examples on Settings › Payments.
 *
 * Both read `feeBreakdown`, which mirrors the Worker's fee constants, so the
 * figure here cannot drift from the one on the order.
 */
export function EntryFeeBreakdown({ fee, currency }: { fee: number; currency: string }) {
  const { t, i18n } = useTranslation()
  if (!(fee > 0)) return null
  const split = feeBreakdown(fee)
  const money = (n: number) => formatMoney(n, currency)
  const pct = new Intl.NumberFormat(i18n.language.startsWith('ro') ? 'ro-RO' : 'en-GB', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(split.keepPercent / 100)

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-content1 p-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm font-semibold">{t('competitionSetup.breakdownTitle')}</p>
        <div className="text-right">
          <p className="text-base font-bold tabular-nums">{money(split.net)}</p>
          <p className="text-xs text-foreground/60">
            {t('payments.examplesYouKeep', { percent: pct })}
          </p>
        </div>
      </div>
      <div
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-default-100"
        aria-hidden="true"
      >
        <div className="bg-primary" style={{ width: `${(split.net / fee) * 100}%` }} />
        <div style={{ width: `${(split.stripe / fee) * 100}%`, backgroundColor: STRIPE_COLOR }} />
        <div
          style={{ width: `${(split.platform / fee) * 100}%`, backgroundColor: PLATFORM_COLOR }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-foreground/70 tabular-nums">
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
          {t('payments.examplesYou', { amount: money(split.net) })}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: STRIPE_COLOR }}
            aria-hidden="true"
          />
          {t('payments.examplesStripe', { amount: money(split.stripe) })}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="size-2 rounded-full"
            style={{ backgroundColor: PLATFORM_COLOR }}
            aria-hidden="true"
          />
          {t('payments.examplesPlatform', { amount: money(split.platform) })}
        </span>
      </div>
      <p className="text-xs text-foreground/60">
        {t('competitionSetup.breakdownPays', { amount: money(fee) })}{' '}
        {t('payments.examplesFootnote')}
      </p>
    </div>
  )
}
